/**
 * EvidenceArtefactDetail page (P75.2). Route: /twin/evidence/:artefactId.
 *
 * Symmetric counterpart to P70's FindingDetail. Composes:
 *   header strip  -- filename + freshness pill (reuses P21 formatProbePill)
 *   metadata grid -- content_type + size + uploader + retrieved_at + source ref
 *   View source   -- external link (http/https uri) OR internal uri text
 *   linked-from   -- assessments + findings that reference this artefact
 *
 * Read-only at v0.77.0. Artefacts are connector-owned (or admin-deleted);
 * inline file preview + metadata editing are out of scope (see the P75 plan).
 */
import { Link, useParams } from "react-router-dom";
import { i18n } from "shared/i18n";
import { formatDateTime } from "shared/time/formatDate";
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatProbePill } from "shared/evidence/probePill";
import type { EvidenceLinkedAssessment, EvidenceLinkedFinding } from "shared/api/types";
import { useEvidenceArtefactDetail } from "twin/evidence/useEvidenceArtefactDetail";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-900",
  high: "bg-amber-100 text-amber-900",
  medium: "bg-slate-100 text-slate-700",
  low: "bg-slate-50 text-slate-600",
};

const ASSESSMENT_STATUS_STYLES: Record<string, string> = {
  compliant: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-800",
  non_compliant: "bg-rose-50 text-rose-700",
  no_evidence: "bg-slate-100 text-slate-600",
};

function isExternal(uri: string): boolean {
  return /^https?:\/\//i.test(uri);
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "--";
  try {
    return formatDateTime(value, i18n.language);
  } catch {
    return value;
  }
}

function FreshnessPill({
  status,
  lastProbedAt,
  lastProbeError,
}: {
  status: "fresh" | "failed" | "pending" | null;
  lastProbedAt: string | null;
  lastProbeError: string | null;
}) {
  const pill = formatProbePill(status, lastProbedAt, lastProbeError);
  if (!pill) return null;
  return (
    <span
      data-testid="evidence-freshness"
      data-status={status ?? "none"}
      title={pill.tooltip}
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        pill.tone,
      ].join(" ")}
    >
      {pill.label}
    </span>
  );
}

function AssessmentRow({ row }: { row: EvidenceLinkedAssessment }) {
  const { t } = useTranslation();
  const tone = ASSESSMENT_STATUS_STYLES[row.status] ?? "bg-slate-100 text-slate-600";
  return (
    <li
      data-testid="evidence-linked-assessment-row"
      data-assessment-id={row.assessment_id}
      className="flex items-center justify-between gap-3 py-1.5"
    >
      <Link
        to={`/framework/assessments/${row.framework_code}/cycles/${row.cycle_id}`}
        className="text-sm font-medium text-brand-navy hover:text-brand-brass"
      >
        {row.framework_code} {t("screens.evidenceArtefactDetail.middotControl")}{" "}
        {row.framework_control_id.slice(0, 8)}
        {t("screens.evidenceArtefactDetail.text")}
      </Link>
      <span
        className={[
          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
          tone,
        ].join(" ")}
      >
        {row.status}
        {row.current_ml !== null ? ` (ML ${row.current_ml})` : ""}
      </span>
    </li>
  );
}

function FindingRow({ row }: { row: EvidenceLinkedFinding }) {
  const tone = SEVERITY_STYLES[row.severity] ?? "bg-slate-100 text-slate-700";
  return (
    <li
      data-testid="evidence-linked-finding-row"
      data-finding-id={row.finding_id}
      className="flex items-center justify-between gap-3 py-1.5"
    >
      <Link
        to={`/twin/findings/${row.finding_id}`}
        className="text-sm font-medium text-brand-navy hover:text-brand-brass truncate"
      >
        {row.title}
      </Link>
      <span
        className={[
          "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase",
          tone,
        ].join(" ")}
      >
        {row.severity}
      </span>
    </li>
  );
}

export function EvidenceArtefactDetail() {
  const { t } = useTranslation();
  const { artefactId } = useParams<{ artefactId: string }>();
  const detail = useEvidenceArtefactDetail(artefactId);

  if (detail.isLoading) {
    return (
      <p data-testid="evidence-loading" className="text-sm text-slate-500">
        {t("modules.twin.evidence.loading")}
      </p>
    );
  }
  if (detail.isError) {
    const status = detail.error?.status ?? 0;
    if (status === 404) {
      return (
        <article data-testid="evidence-not-found" className="space-y-2">
          <h1 className="text-2xl font-semibold text-slate-900">
            <MarsadIcon
              name="document-locker"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {t("modules.twin.evidence.notFoundHeading")}
          </h1>
          <p className="text-sm text-slate-600">
            {t("modules.twin.evidence.notFoundBody", { id: artefactId })}
          </p>
        </article>
      );
    }
    return (
      <p data-testid="evidence-error" className="text-sm text-rose-700">
        {t("modules.twin.evidence.error")}
      </p>
    );
  }
  if (!detail.data) {
    return null;
  }
  const a = detail.data;
  const external = isExternal(a.uri);

  return (
    <article data-testid="evidence-detail" className="space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <FreshnessPill
            status={a.last_probe_status}
            lastProbedAt={a.last_probed_at}
            lastProbeError={a.last_probe_error}
          />
          {a.content_type && (
            <code className="text-xs text-slate-500">{a.content_type}</code>
          )}
        </div>
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="document-locker"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {a.filename ?? t("modules.twin.evidence.untitled")}
        </h1>
      </header>

      <dl
        data-testid="evidence-meta"
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm"
      >
        {a.folder_path && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("modules.twin.evidence.folder")}
            </dt>
            <dd data-testid="evidence-folder" className="text-slate-900 break-all">
              {a.folder_path === "/"
                ? t("modules.twin.evidence.folderTop")
                : a.folder_path}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            {t("modules.twin.evidence.size")}
          </dt>
          <dd className="text-slate-900">{formatBytes(a.size_bytes)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            {t("modules.twin.evidence.uploader")}
          </dt>
          <dd className="text-slate-900">{a.uploader_email ?? "--"}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            {t("modules.twin.evidence.retrievedAt")}
          </dt>
          {/* Round 20 Y4 -- "--" said nothing of the artefact's age; the
              upload date stands in, labelled as such. */}
          <dd data-testid="evidence-dated" className="text-slate-900">
            {a.retrieved_at
              ? formatTimestamp(a.retrieved_at)
              : a.uploaded_at
                ? t("modules.twin.evidence.uploadedInstead", {
                    when: formatTimestamp(a.uploaded_at),
                  })
                : "--"}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">
            {t("modules.twin.evidence.viewSource")}
          </dt>
          <dd>
            {external ? (
              <a
                href={a.uri}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="evidence-source-link"
                className="text-sm font-medium text-brand-navy hover:text-brand-brass"
              >
                {t("modules.twin.evidence.viewSourceLink")}{" "}
                {t("screens.evidenceArtefactDetail.rarr")}
              </a>
            ) : (
              <span
                data-testid="evidence-source-internal"
                className="text-sm text-slate-600 break-all"
              >
                {a.uri}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {a.notes && (
        <section data-testid="evidence-notes" className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.evidence.notesHeading")}
          </h2>
          <p className="text-sm text-slate-800 whitespace-pre-line">{a.notes}</p>
        </section>
      )}

      <section data-testid="evidence-linked-assessments" className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.evidence.linkedAssessments", {
            count: a.linked_assessments.length,
          })}
        </h2>
        {a.linked_assessments.length === 0 ? (
          <p className="text-sm text-slate-500">
            {t("modules.twin.evidence.noAssessments")}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {a.linked_assessments.map((row) => (
              <AssessmentRow key={row.link_id} row={row} />
            ))}
          </ul>
        )}
      </section>

      <section data-testid="evidence-linked-findings" className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.evidence.linkedFindings", {
            count: a.linked_findings.length,
          })}
        </h2>
        {a.linked_findings.length === 0 ? (
          <p className="text-sm text-slate-500">
            {t("modules.twin.evidence.noFindings")}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {a.linked_findings.map((row) => (
              <FindingRow key={row.link_id} row={row} />
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}
